import { useMemo, useState, type ReactNode } from 'react';
import { BaseCard, SideListCard, type SideListItem } from '@practium/ui';
import { STATE_NAMES } from '../../../constants/stateNames';
import type { ReferenceFormValue, StateReferenceDTO } from '../../../types/parameters.types';
import { isBrazilMapAvailable } from '../../../utils/brazilMap';
import { pluralize } from '../../../utils/format';
import { BrazilMapSVG } from '../../Map/BrazilMap/BrazilMapSVG';
import { RegionFormModal } from '../../Region/RegionFormModal';
import styles from '../../Sections/Sections.module.css';

interface RegionsSidebarProps {
	references: StateReferenceDTO[];
	selected: StateReferenceDTO | null;
	isLoading: boolean;
	isCreateModalOpen: boolean;
	viewSwitch: ReactNode;
	onSelect: (id: string) => void;
	onCreate: (form: ReferenceFormValue) => Promise<boolean>;
	onUpdate: (form: ReferenceFormValue) => Promise<boolean>;
	onDelete: () => Promise<boolean>;
	onCloseCreateModal: () => void;
}

export const RegionsSidebar = ({
	references,
	selected,
	isLoading,
	isCreateModalOpen,
	viewSwitch,
	onSelect,
	onCreate,
	onUpdate,
	onDelete,
	onCloseCreateModal,
}: RegionsSidebarProps) => {
	const [isEditOpen, setIsEditOpen] = useState(false);

	const items = useMemo<SideListItem[]>(
		() =>
			references.map((reference) => ({
				id: reference._id,
				label: reference.referenceCode,
				sublabel: reference.stateCode.map((code) => STATE_NAMES[code] ?? code).join(', '),
				icon: isBrazilMapAvailable ? (
					<div className={styles.regionThumb}>
						<BrazilMapSVG selectedStateCodes={reference.stateCode} />
					</div>
				) : undefined,
			})),
		[references]
	);

	return (
		<>
			<div className={styles.sidebarList}>
				<SideListCard
					stretch
					title="Regiões"
					subtitle="Selecione a região que deseja visualizar"
					items={items}
					activeId={selected?._id}
					onSelect={onSelect}
					filterSlot={viewSwitch}
					actionButton={{ label: 'Editar', onClick: () => setIsEditOpen(true), disabled: !selected }}
					isLoading={isLoading}
				/>
			</div>

			{selected && (
				<div className={styles.sidebarMapSlot}>
					<BaseCard
						stretch
						title={isBrazilMapAvailable ? 'Mapa da região' : 'Estados da região'}
						subtitle={pluralize(selected.stateCode.length, 'estado', 'estados')}
					>
						{
							isBrazilMapAvailable ? (
								<div className={styles.sidebarMap}>
									<BrazilMapSVG selectedStateCodes={selected.stateCode} />
								</div>
							) : (
								<div className={styles.sidebarStates}>
									{selected.stateCode.map((code) => (
										<span key={code} className={styles.sidebarState}>
											{code}
										</span>
									))}
								</div>
							)
						}
					</BaseCard>
				</div>
			)}

			{isCreateModalOpen && (
				<RegionFormModal references={references} onClose={onCloseCreateModal} onConfirm={onCreate} />
			)}

			{isEditOpen && selected && (
				<RegionFormModal
					references={references}
					initialReference={selected}
					onClose={() => setIsEditOpen(false)}
					onConfirm={onUpdate}
					onDelete={onDelete}
				/>
			)}
		</>
	);
};
